import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Camera, MapPinOff, X } from 'lucide-react';
import { api, hlaskaZChyby } from '../api/client.js';
import type { FotoGalerie, Fotogalerie, Zakazka } from '../api/types.js';
import { useVybranaZakazka } from '../lib/globalniZakazka.js';
import { Chyba, EmptyState, Nacitani } from '../components/ui.js';

/**
 * Fotogalerie stavby — všechny fotky zakázky (deník, nálezy, změny, vady,
 * úkoly) na jednom místě. Na stavbě se fotí DO kontextu, ale hledá se PŘES
 * kontexty („kde je fotka té jímky?") — proto souhrn seskupený po dnech
 * s filtrem podle objektu. Klik = velký náhled se zdrojem.
 */

/** České datum dne galerie (klíč YYYY-MM-DD → „6. července 2026"). */
function nadpisDne(den: string): string {
  return new Date(`${den}T00:00:00Z`).toLocaleDateString('cs-CZ', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** Odkaz na místo, kde fotka vznikla (jen tam, kde detailová stránka existuje). */
function odkazZdroje(foto: FotoGalerie): string | null {
  if (foto.zdroj === 'zmena') return `/zmeny/${foto.zdrojId}`;
  if (foto.zdroj === 'nalez') return '/nalezy';
  if (foto.zdroj === 'denik') return '/denik';
  return null;
}

export function FotogaleriePage() {
  const { data: zakazky } = useQuery({
    queryKey: ['zakazky'],
    queryFn: () => api.get<Zakazka[]>('/api/projects'),
  });
  const { vybranaId: zakazkaId } = useVybranaZakazka(zakazky);

  const { data, isLoading, error } = useQuery({
    queryKey: ['fotogalerie', zakazkaId],
    queryFn: () => api.get<Fotogalerie>(`/api/projects/${zakazkaId}/fotogalerie`),
    enabled: !!zakazkaId,
  });

  const [objektId, setObjektId] = useState<string | null>(null);
  const [den, setDen] = useState('');
  const [otevrena, setOtevrena] = useState<FotoGalerie | null>(null);

  const fotky = useMemo(() => {
    let vysledek = data?.fotky ?? [];
    if (objektId) vysledek = vysledek.filter((f) => f.objektId === objektId);
    if (den) vysledek = vysledek.filter((f) => f.den === den);
    return vysledek;
  }, [data, objektId, den]);

  // Seskupení po dnech (pole je už seřazené od nejnovějších).
  const dny = useMemo(() => {
    const skupiny: { den: string; fotky: FotoGalerie[] }[] = [];
    for (const f of fotky) {
      const posledni = skupiny[skupiny.length - 1];
      if (posledni && posledni.den === f.den) posledni.fotky.push(f);
      else skupiny.push({ den: f.den, fotky: [f] });
    }
    return skupiny;
  }, [fotky]);

  if (!zakazkaId && !zakazky) return <Nacitani text="Načítám zakázky…" />;

  return (
    <div className="contents">
      <div>
        <h1 className="text-2xl font-bold">Fotky stavby</h1>
        <p className="text-base text-text-secondary">
          Všechny fotky zakázky — z deníku, nálezů, změn i vad.
        </p>
      </div>

      {error && <Chyba>{hlaskaZChyby(error)}</Chyba>}
      {isLoading && <Nacitani text="Načítám fotky…" />}

      {data && (
        <>
          {/* Filtr: objekt jako tiché přepínače + den. */}
          <div className="flex flex-wrap items-center gap-2">
            {data.objekty.length > 0 && (
              <div role="radiogroup" aria-label="Filtr podle objektu" className="flex gap-1">
                {[{ id: null as string | null, kod: 'Vše' }, ...data.objekty].map((o) => (
                  <button
                    key={o.id ?? 'vse'}
                    type="button"
                    role="radio"
                    aria-checked={objektId === o.id}
                    onClick={() => setObjektId(o.id)}
                    className={`min-h-10 rounded-lg px-3 text-sm font-semibold transition-colors duration-100 ${
                      objektId === o.id
                        ? 'bg-primary-tint text-primary'
                        : 'text-text-secondary hover:bg-bg'
                    }`}
                  >
                    {o.kod}
                  </button>
                ))}
              </div>
            )}
            <input
              type="date"
              aria-label="Filtr podle dne"
              className="min-h-10 rounded-lg border border-border bg-surface px-3 text-sm"
              value={den}
              onChange={(e) => setDen(e.target.value)}
            />
            {(objektId || den) && (
              <button
                type="button"
                className="text-sm font-semibold text-primary underline underline-offset-2"
                onClick={() => {
                  setObjektId(null);
                  setDen('');
                }}
              >
                zrušit filtr
              </button>
            )}
          </div>

          {dny.length === 0 && (
            <EmptyState
              ikona={<Camera size={28} strokeWidth={1.5} aria-hidden="true" />}
              titulek={data.fotky.length === 0 ? 'Zatím žádné fotky' : 'Filtru nic neodpovídá'}
              popis={
                data.fotky.length === 0
                  ? 'Fotky se sem sbírají samy — stačí fotit v terénu k deníku, nálezům a vadám.'
                  : 'Zkuste zrušit filtr nebo vybrat jiný den či objekt.'
              }
            />
          )}

          {dny.map((skupina) => (
            <section key={skupina.den} aria-label={nadpisDne(skupina.den)}>
              <h2 className="mb-2 text-sm font-bold text-text-secondary">
                {nadpisDne(skupina.den)}
                <span className="ml-2 font-normal">
                  {skupina.fotky.length}{' '}
                  {skupina.fotky.length === 1
                    ? 'fotka'
                    : skupina.fotky.length <= 4
                      ? 'fotky'
                      : 'fotek'}
                </span>
              </h2>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
                {skupina.fotky.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    className="group relative aspect-square overflow-hidden rounded-lg border border-border bg-surface"
                    onClick={() => setOtevrena(f)}
                  >
                    <img
                      src={`/api/diary/prilohy/${f.id}/soubor`}
                      alt={f.popis ?? f.nazev}
                      loading="lazy"
                      className="size-full object-cover transition-transform duration-100 group-hover:scale-105"
                    />
                    <span className="absolute inset-x-0 bottom-0 truncate bg-text-heading/60 px-1.5 py-0.5 text-left text-[11px] text-white">
                      {f.zdrojPopis}
                    </span>
                  </button>
                ))}
              </div>
            </section>
          ))}
        </>
      )}

      {/* Velký náhled — fotka + odkud je + skok na zdroj. */}
      {otevrena && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={otevrena.popis ?? otevrena.nazev}
          className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-3 bg-text-heading/90 p-4"
          onClick={() => setOtevrena(null)}
        >
          <button
            type="button"
            aria-label="Zavřít náhled"
            className="absolute top-4 right-4 flex size-10 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25"
            onClick={() => setOtevrena(null)}
          >
            <X size={20} strokeWidth={2} aria-hidden="true" />
          </button>
          <img
            src={`/api/diary/prilohy/${otevrena.id}/soubor`}
            alt={otevrena.popis ?? otevrena.nazev}
            className="max-h-[75vh] max-w-full rounded-lg object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <div
            className="flex max-w-full flex-col items-center gap-1 text-center text-white"
            onClick={(e) => e.stopPropagation()}
          >
            {otevrena.popis && <p className="text-sm font-semibold">{otevrena.popis}</p>}
            <p className="flex items-center gap-2 text-sm text-white/80">
              {nadpisDne(otevrena.den)} · {otevrena.zdrojPopis}
              {otevrena.bezGps && (
                <span className="flex items-center gap-1 text-white/60">
                  <MapPinOff size={13} strokeWidth={2} aria-hidden="true" /> bez GPS
                </span>
              )}
            </p>
            {odkazZdroje(otevrena) && (
              <Link
                to={odkazZdroje(otevrena)!}
                className="text-sm font-semibold text-white underline underline-offset-2"
              >
                Otevřít zdroj fotky
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
